import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { format, isToday, isYesterday } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import api from '../../services/api';

const ONLINE_THRESHOLD_MS = 90_000;
const POLL_INTERVAL_MS    = 3_000;
const ALL_ID              = 'all';

function tocarNotificacao() {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    [{ freq: 880, start: 0, dur: 0.08 }, { freq: 1100, start: 0.1, dur: 0.1 }].forEach(({ freq, start, dur }) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain); gain.connect(ctx.destination);
      osc.type = 'sine';
      osc.frequency.setValueAtTime(freq, ctx.currentTime + start);
      gain.gain.setValueAtTime(0.25, ctx.currentTime + start);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + start + dur);
      osc.start(ctx.currentTime + start);
      osc.stop(ctx.currentTime + start + dur + 0.01);
    });
  } catch (_) {}
}

function isOnline(u) {
  if (!u?.online_em) return false;
  return Date.now() - new Date(u.online_em).getTime() < ONLINE_THRESHOLD_MS;
}

function formatHora(iso) {
  const d = new Date(iso);
  if (isToday(d))     return format(d, 'HH:mm');
  if (isYesterday(d)) return 'Ontem';
  return format(d, 'dd/MM/yy', { locale: ptBR });
}

function Avatar({ usuario, size = 'md', showOnline = false, isAll = false }) {
  const dim   = size === 'sm' ? 32 : size === 'lg' ? 48 : 40;
  const fs    = size === 'sm' ? 12 : size === 'lg' ? 18 : 14;
  const dotSz = size === 'sm' ? 8 : 10;
  const online = showOnline && isOnline(usuario);

  if (isAll) {
    return (
      <div style={{ position: 'relative', flexShrink: 0 }}>
        <div style={{
          width: dim, height: dim, fontSize: fs, borderRadius: '28%',
          background: 'linear-gradient(135deg,#6C4CF5,#A78BFA)', color: '#fff', fontWeight: 800,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>@</div>
      </div>
    );
  }

  return (
    <div style={{ position: 'relative', flexShrink: 0 }}>
      <div style={{
        width: dim, height: dim, fontSize: fs, borderRadius: '28%',
        background: '#6C4CF5', color: '#fff', fontWeight: 700,
        display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden',
      }}>
        {usuario?.avatar_url
          ? <img src={usuario.avatar_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          : (usuario?.nome?.charAt(0) || '?')}
      </div>
      {showOnline && (
        <span style={{
          position: 'absolute', bottom: 0, right: 0,
          width: dotSz, height: dotSz, borderRadius: '50%',
          border: '2px solid #111827',
          background: online ? '#4ADE80' : '#475569',
        }} />
      )}
    </div>
  );
}

export default function Chat() {
  const [showList, setShowList] = useState(true);
  const isMobile = typeof window !== 'undefined' && window.innerWidth <= 640;
  const [euId, setEuId]         = useState(null);
  const [euNome, setEuNome]     = useState('');
  const [contatos, setContatos] = useState([]);
  const [ultimaBroadcast, setUltimaBroadcast] = useState(null);
  const [conversa, setConversa] = useState([]);
  const [contatoId, setContatoId] = useState(null);
  const [texto, setTexto]       = useState('');
  const [busca, setBusca]       = useState('');
  const [enviando, setEnviando] = useState(false);

  const messagesEndRef = useRef(null);
  const inputRef       = useRef(null);
  const contatoIdRef   = useRef(null);

  useEffect(() => {
    api.get('/auth/me').then(r => {
      setEuId(r.data.id);
      setEuNome(r.data.nome);
    }).catch(() => {});
  }, []);

  const carregarContatos = useCallback(async () => {
    try {
      const { data } = await api.get('/chat/contatos');
      // backend retorna { contatos, ultima_broadcast }
      if (data?.contatos) {
        setContatos(data.contatos);
        setUltimaBroadcast(data.ultima_broadcast || null);
      } else if (Array.isArray(data)) {
        // compatibilidade caso retorne array direto
        setContatos(data);
      }
    } catch {}
  }, []);

  const carregarConversa = useCallback(async (cId) => {
    if (!cId) return;
    try {
      const endpoint = cId === ALL_ID ? '/chat/mensagens/all' : `/chat/mensagens/${cId}`;
      const { data } = await api.get(endpoint);
      setConversa(data || []);
    } catch {}
  }, []);

  const marcarLidas = useCallback(async (cId) => {
    if (!cId || !euId || cId === ALL_ID) return;
    try { await api.patch('/chat/mensagens/lidas', { de_id: cId }); }
    catch {}
  }, [euId]);

  // Ping de presença
  useEffect(() => {
    if (!euId) return;
    api.patch('/chat/online').catch(() => {});
    const t = setInterval(() => api.patch('/chat/online').catch(() => {}), 30_000);
    return () => clearInterval(t);
  }, [euId]);

  // Polling
  useEffect(() => {
    if (!euId) return;
    carregarContatos();
    const t = setInterval(async () => {
      await carregarContatos();
      if (contatoIdRef.current) await carregarConversa(contatoIdRef.current);
    }, POLL_INTERVAL_MS);
    return () => clearInterval(t);
  }, [euId, carregarContatos, carregarConversa]);

  // Som ao receber mensagem nova
  const prevConversaLen = useRef(0);
  useEffect(() => {
    if (!euId || !contatoId) return;
    const novas = conversa.slice(prevConversaLen.current);
    const temNova = novas.some(m => m.de_id !== euId);
    if (temNova && prevConversaLen.current > 0) tocarNotificacao();
    prevConversaLen.current = conversa.length;
  }, [conversa, euId, contatoId]);

  // Auto-scroll
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [conversa]);

  const abrirConversa = async (cId) => {
    contatoIdRef.current = cId;
    prevConversaLen.current = 0;
    setContatoId(cId);
    setConversa([]);
    if (window.innerWidth <= 640) setShowList(false);
    await carregarConversa(cId);
    await marcarLidas(cId);
    await carregarContatos();
    inputRef.current?.focus();
  };

  useEffect(() => {
    if (contatoId && euId) {
      carregarConversa(contatoId);
      marcarLidas(contatoId);
    }
  }, [contatoId, euId, carregarConversa, marcarLidas]);

  const enviarMensagem = async (e) => {
    e.preventDefault();
    if (!texto.trim() || !contatoId || enviando) return;
    setEnviando(true);
    const textoEnvio = texto.trim();
    setTexto('');
    try {
      const { data } = await api.post('/chat/mensagens', { para_id: contatoId, texto: textoEnvio });
      setConversa(prev => [...prev, data]);
      await carregarContatos();
    } catch {
      setTexto(textoEnvio);
    } finally {
      setEnviando(false);
      inputRef.current?.focus();
    }
  };

  const contatosFiltrados = useMemo(() =>
    contatos
      .filter(u => !busca || u.nome.toLowerCase().includes(busca.toLowerCase()))
      .sort((a, b) => {
        const ta = a.ultima_mensagem?.criado_em || '0';
        const tb = b.ultima_mensagem?.criado_em || '0';
        return tb.localeCompare(ta);
      }),
    [contatos, busca]
  );

  const totalNaoLidas = useMemo(() =>
    contatos.reduce((s, u) => s + (u.nao_lidas || 0), 0),
    [contatos]
  );

  const isAll = contatoId === ALL_ID;
  const contato = isAll ? null : contatos.find(u => u.id === contatoId);

  const nomeContato = isAll ? 'Todos' : (contato?.nome || '');

  return (
    <div style={{ display: 'flex', height: 'calc(100vh - 88px)', borderRadius: 16, overflow: 'hidden', border: '1px solid #2A3652', background: '#111827' }}>

      {/* ── Painel esquerdo — contatos ── */}
      <div className={showList ? 'chat-sidebar' : 'chat-sidebar-hidden'} style={{ width: 280, flexShrink: 0, borderRight: '1px solid #2A3652', display: 'flex', flexDirection: 'column', background: '#111827' }}>
        {/* Header */}
        <div style={{ padding: '16px 14px 12px', borderBottom: '1px solid #2A3652' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
            <h2 style={{ fontSize: 16, fontWeight: 800, color: '#E2E8F4' }}>Chat</h2>
            {totalNaoLidas > 0 && (
              <span style={{ background: '#6C4CF5', color: '#fff', fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 999 }}>{totalNaoLidas}</span>
            )}
          </div>
          <input
            value={busca}
            onChange={e => setBusca(e.target.value)}
            placeholder="Buscar colaborador..."
            style={{ width: '100%', padding: '8px 12px', fontSize: 13, background: 'rgba(255,255,255,.05)', border: '1px solid #2A3652', borderRadius: 10, color: '#CBD5E1', outline: 'none', boxSizing: 'border-box' }}
          />
        </div>

        {/* Lista */}
        <div style={{ flex: 1, overflowY: 'auto' }}>
          {/* Entrada "Todos" — sempre no topo */}
          {!busca && (() => {
            const ativo = contatoId === ALL_ID;
            return (
              <button onClick={() => abrirConversa(ALL_ID)} style={{
                width: '100%', display: 'flex', alignItems: 'center', gap: 10,
                padding: '11px 14px', textAlign: 'left', border: 'none', cursor: 'pointer',
                background: ativo ? 'rgba(108,76,245,.14)' : 'rgba(108,76,245,.06)',
                borderLeft: `2px solid ${ativo ? '#6C4CF5' : '#6C4CF5AA'}`,
                borderBottom: '1px solid rgba(255,255,255,.06)',
                transition: 'background .12s',
              }}>
                <Avatar isAll size="md" />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <p style={{ fontSize: 13, fontWeight: 700, color: '#A78BFA', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>Todos</p>
                    {ultimaBroadcast && <span style={{ fontSize: 10, color: '#475569', flexShrink: 0, marginLeft: 4 }}>{formatHora(ultimaBroadcast.criado_em)}</span>}
                  </div>
                  <p style={{ fontSize: 11, color: '#6C4CF5', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {ultimaBroadcast ? ultimaBroadcast.texto : <em style={{ color: '#475569' }}>Canal geral da equipe</em>}
                  </p>
                </div>
              </button>
            );
          })()}

          {contatosFiltrados.length === 0 && (
            <p style={{ fontSize: 12, color: '#475569', textAlign: 'center', marginTop: 32, padding: '0 16px' }}>Nenhum colaborador encontrado.</p>
          )}
          {contatosFiltrados.map(u => {
            const ativo = contatoId === u.id;
            const online = isOnline(u);
            return (
              <button key={u.id} onClick={() => abrirConversa(u.id)} style={{
                width: '100%', display: 'flex', alignItems: 'center', gap: 10,
                padding: '11px 14px', textAlign: 'left', border: 'none', cursor: 'pointer',
                background: ativo ? 'rgba(108,76,245,.14)' : 'transparent',
                borderLeft: `2px solid ${ativo ? '#6C4CF5' : 'transparent'}`,
                borderBottom: '1px solid rgba(255,255,255,.03)',
                transition: 'background .12s',
              }}
              onMouseEnter={e => { if (!ativo) e.currentTarget.style.background = 'rgba(255,255,255,.04)'; }}
              onMouseLeave={e => { if (!ativo) e.currentTarget.style.background = 'transparent'; }}>
                <Avatar usuario={u} showOnline size="md" />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <p style={{ fontSize: 13, fontWeight: 600, color: ativo ? '#E2E8F4' : '#CBD5E1', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{u.nome.split(' ')[0]}</p>
                    {u.ultima_mensagem && <span style={{ fontSize: 10, color: '#475569', flexShrink: 0, marginLeft: 4 }}>{formatHora(u.ultima_mensagem.criado_em)}</span>}
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 2 }}>
                    <p style={{ fontSize: 11, color: '#475569', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>
                      {u.ultima_mensagem
                        ? (u.ultima_mensagem.de_id === euId ? `Você: ${u.ultima_mensagem.texto}` : u.ultima_mensagem.texto)
                        : <em>Inicie uma conversa</em>}
                    </p>
                    {u.nao_lidas > 0 && (
                      <span style={{ background: '#6C4CF5', color: '#fff', fontSize: 10, fontWeight: 700, minWidth: 18, height: 18, borderRadius: 999, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginLeft: 6, padding: '0 4px' }}>
                        {u.nao_lidas > 9 ? '9+' : u.nao_lidas}
                      </span>
                    )}
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* ── Painel direito — conversa ── */}
      {!contatoId ? (
        <div className={showList ? 'resp-hide-mobile' : 'chat-panel-full'} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 12, background: '#0F1623' }}>
          <div style={{ width: 64, height: 64, borderRadius: '50%', background: 'rgba(255,255,255,.05)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#475569" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
            </svg>
          </div>
          <p style={{ fontSize: 14, fontWeight: 600, color: '#475569' }}>Selecione um colaborador</p>
          <p style={{ fontSize: 12, color: '#334155' }}>Escolha uma conversa para começar</p>
        </div>
      ) : (
        <div className={!showList ? 'chat-panel-full' : ''} style={{ flex: 1, display: 'flex', flexDirection: 'column', background: '#0F1623' }}>
          {/* Header da conversa */}
          <div style={{ padding: '12px 18px', background: '#111827', borderBottom: '1px solid #2A3652', display: 'flex', alignItems: 'center', gap: 12 }}>
            <button className="resp-show-mobile" onClick={() => setShowList(true)} style={{ background: 'none', border: 'none', color: '#6C4CF5', cursor: 'pointer', padding: '4px 8px 4px 0', fontSize: 20, lineHeight: 1 }}>‹</button>
            <Avatar usuario={contato} isAll={isAll} showOnline={!isAll} size="md" />
            <div>
              <p style={{ fontSize: 14, fontWeight: 700, color: isAll ? '#A78BFA' : '#E2E8F4' }}>{nomeContato}</p>
              <p style={{ fontSize: 11, color: isAll ? '#6C4CF5' : (isOnline(contato) ? '#4ADE80' : '#475569'), marginTop: 1 }}>
                {isAll
                  ? `Canal geral — ${contatos.length} colaboradores`
                  : isOnline(contato)
                    ? '● Online agora'
                    : contato?.online_em
                      ? `Visto ${formatHora(contato.online_em)}`
                      : 'Offline'}
              </p>
            </div>
          </div>

          {/* Mensagens */}
          <div style={{ flex: 1, overflowY: 'auto', padding: '16px 18px' }}>
            <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', minHeight: '100%', gap: 2 }}>
              {conversa.length === 0 && (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '64px 0', gap: 8, opacity: .4 }}>
                  <p style={{ fontSize: 13, color: '#475569' }}>Nenhuma mensagem ainda.</p>
                  <p style={{ fontSize: 12, color: '#334155' }}>
                    {isAll ? 'Mande uma mensagem para toda a equipe!' : `Diga olá para ${nomeContato.split(' ')[0]}!`}
                  </p>
                </div>
              )}
              {conversa.map((msg, i) => {
                const minha = msg.de_id === euId;
                const anterior = conversa[i - 1];
                const mesmoRem = anterior?.de_id === msg.de_id;
                const diff = anterior ? new Date(msg.criado_em) - new Date(anterior.criado_em) : Infinity;
                const mostraHora = !mesmoRem || diff > 120_000;
                // No canal Todos, mostrar nome do remetente
                const remetenteNome = isAll && !minha
                  ? (msg.de_nome || contatos.find(c => c.id === msg.de_id)?.nome || 'Alguém')
                  : null;

                return (
                  <div key={msg.id} style={{ display: 'flex', justifyContent: minha ? 'flex-end' : 'flex-start', marginTop: mesmoRem && !mostraHora ? 2 : 12 }}>
                    <div style={{ maxWidth: '70%', display: 'flex', flexDirection: 'column', alignItems: minha ? 'flex-end' : 'flex-start' }}>
                      {remetenteNome && !mesmoRem && (
                        <span style={{ fontSize: 10, color: '#A78BFA', fontWeight: 600, marginBottom: 3, paddingLeft: 4 }}>{remetenteNome}</span>
                      )}
                      <div style={{
                        padding: '9px 14px', borderRadius: 18, fontSize: 13, lineHeight: 1.5, wordBreak: 'break-word',
                        background: minha ? '#6C4CF5' : '#1E2D45',
                        color: minha ? '#fff' : '#CBD5E1',
                        borderBottomRightRadius: minha ? 4 : 18,
                        borderBottomLeftRadius: minha ? 18 : 4,
                      }}>
                        {msg.texto}
                      </div>
                      {mostraHora && (
                        <span style={{ fontSize: 10, color: '#475569', marginTop: 3, padding: '0 4px' }}>
                          {format(new Date(msg.criado_em), 'HH:mm')}
                          {minha && !isAll && <span style={{ marginLeft: 4 }}>{msg.lida ? '✓✓' : '✓'}</span>}
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
              <div ref={messagesEndRef} />
            </div>
          </div>

          {/* Input */}
          <form onSubmit={enviarMensagem} style={{ padding: '12px 14px', background: '#111827', borderTop: '1px solid #2A3652', display: 'flex', alignItems: 'center', gap: 10 }}>
            <input
              ref={inputRef}
              value={texto}
              onChange={e => setTexto(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); enviarMensagem(e); } }}
              placeholder={isAll ? 'Mensagem para toda a equipe...' : `Mensagem para ${nomeContato.split(' ')[0]}...`}
              autoComplete="off"
              style={{ flex: 1, padding: '10px 14px', fontSize: 13, background: 'rgba(255,255,255,.05)', border: '1px solid #2A3652', borderRadius: 12, color: '#CBD5E1', outline: 'none', boxSizing: 'border-box' }}
            />
            <button type="submit" disabled={!texto.trim() || enviando} style={{
              width: 40, height: 40, borderRadius: 12, background: '#6C4CF5', border: 'none', cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
              opacity: !texto.trim() || enviando ? 0.4 : 1, transition: 'opacity .15s',
            }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="white" style={{ transform: 'rotate(45deg)' }}>
                <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z"/>
              </svg>
            </button>
          </form>
        </div>
      )}
    </div>
  );
}
